import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { ClipboardList, Wallet, Loader2, FileText, Building2 } from "lucide-react";

const MODULES = [
  {
    key: "rehearsal_reports",
    title: "Rehearsal Reports",
    icon: ClipboardList,
    body: "No-code report builder tied to rehearsals. Admin/staff design report templates and fill them out during or after rehearsal. Includes an @-mention actionable system that emails and notifies assignees.",
    routes: "/rehearsal-reports · /actionables",
  },
  {
    key: "budget",
    title: "Budget Tracker",
    icon: Wallet,
    body: "Checkbook-style ledger with categories, receipt uploads, running balance, month-to-date, top-spend categories, and CSV export. Staff+ can add and edit; cast has no access.",
    routes: "/budget",
  },
  {
    key: "script",
    title: "Script Intelligence",
    icon: FileText,
    body: "PDF script analysis with heuristic parser: auto-detects acts, scenes, songs, characters, line counts, entrances, exits, props, costume references, and lighting / sound / video cues. Feeds the Character Detail deep-view. Zero recurring cost — enhanced LLM analysis is a future opt-in per script.",
    routes: "/script · /characters/:id",
  },
  {
    key: "facilities",
    title: "Facilities & Rooms",
    icon: Building2,
    body: "Multi-show room management. Admins define facilities and rooms once, then every production picks a room when scheduling a rehearsal. Cross-production schedule view highlights double-books before they happen.",
    routes: "/facilities",
  },
];

export default function PlatformModules() {
  const [flags, setFlags] = useState(null);
  const [saving, setSaving] = useState(null); // key currently saving

  useEffect(() => { api.get("/platform/modules").then((r) => setFlags(r.data)); }, []);

  const toggle = async (k, v) => {
    setSaving(k);
    try {
      const next = { ...flags, [k]: v };
      const { data } = await api.put("/platform/modules", next);
      setFlags(data);
      toast.success(`${MODULES.find((m) => m.key === k).title} ${v ? "enabled" : "disabled"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(null); }
  };

  if (!flags) return <div className="text-zinc-500 text-sm">Loading modules…</div>;

  return (
    <div className="space-y-8" data-testid="platform-modules">
      <div>
        <div className="text-[10px] uppercase tracking-[0.24em] text-gold mb-1">Platform Admin</div>
        <h1 className="font-display text-4xl sm:text-5xl">Modules</h1>
        <p className="text-zinc-500 text-sm mt-2 max-w-2xl">Enable or disable optional modules across every production. Users see the module in their sidebar only when it&apos;s turned on; write endpoints stay locked until you enable it.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {MODULES.map(({ key, title, icon: Icon, body, routes }) => {
          const on = !!flags[key];
          return (
            <div key={key} data-testid={`module-card-${key}`}
                 className={`rounded-lg border p-6 transition-colors ${on ? "border-gold/40 bg-gradient-to-br from-[#141210] to-[#0F0F0F]" : "border-white/10 bg-[#0F0F0F]"}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className={`w-11 h-11 rounded-md grid place-items-center shrink-0 ${on ? "bg-gold/15 border border-gold/40" : "bg-white/5 border border-white/10"}`}>
                    <Icon className={`w-5 h-5 ${on ? "text-gold" : "text-zinc-500"}`} />
                  </div>
                  <div>
                    <div className="font-display text-xl">{title}</div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1">{on ? "Enabled" : "Disabled"} · {routes}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {saving === key && <Loader2 className="w-4 h-4 animate-spin text-gold" />}
                  <Switch data-testid={`toggle-${key}`} checked={on} onCheckedChange={(v) => toggle(key, v)} disabled={saving === key} />
                </div>
              </div>
              <p className="mt-4 text-sm text-zinc-400 leading-relaxed">{body}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
